import { isUsd, unitOf, type Currency } from '../api/types'

/**
 * THE EXPORT MODEL — what every "Extract as Excel / PDF" button hands to the
 * generators.
 *
 * The rule the whole feature stands on: an export is the SCREEN, re-typeset.
 * Builders construct an ExportDoc from the component state that is already
 * rendered — the same rows, the same period, the same filters, the same
 * masking — never from a fresh query. What the reader was permitted to see is
 * exactly what leaves the browser; a figure the screen masked is `null` here
 * and becomes an em dash there, never a zero and never an omission that
 * changes a total.
 *
 * The generators (excel.ts, pdf.ts) are heavy and load on demand — nothing in
 * THIS file may import them, and page code imports only this file eagerly.
 */

/**
 * 'qty' (5 Oct 2026) is a stock quantity as the warehouse counts it: up to four
 * decimals and no padding, as the Power BI Operations report printed its lot
 * quantities. 'num2' rounds to two, which loses the third and fourth decimal
 * on 438 lots of Inventory Aging.
 */
export type CellKind = 'text' | 'money' | 'int' | 'pct' | 'num2' | 'qty'

export interface ExportColumn {
  header: string
  /** Drives alignment and number formatting in both targets. Default 'text'. */
  kind?: CellKind
}

export interface ExportRow {
  /** Raw values, one per column. `null` = masked → em dash, never zero. */
  cells: (string | number | null)[]
  /** Indent steps for the first cell — the explorer's drill depth. */
  indent?: number
  /** Emphasis — a parent row above its drilled children. */
  bold?: boolean
}

export interface ExportTable {
  /** Small heading above the table when a document carries several. */
  title?: string
  /** One honest sentence under the table: caps, exclusions, definitions. */
  note?: string
  columns: ExportColumn[]
  rows: ExportRow[]
}

export interface ExportKpi {
  label: string
  /** Pre-formatted, exactly as the tile shows it. */
  value: string
  secondary?: string
}

export interface ExportChart {
  /** PNG data URL, rasterized from the on-screen SVG. */
  dataUrl: string
  width: number
  height: number
  caption?: string
}

export interface ExportDoc {
  /** 'Sales', 'Receivables' — the eyebrow. */
  report: string
  /** 'Explorer', 'Leaderboards' — names the file and the sheet. */
  page: string
  /** The document heading, usually the card title. */
  title: string
  subtitle?: string
  /**
   * The selection, stated line by line: period, currency, every standing
   * filter, drill path, search. The reader of the FILE was not at the screen —
   * without these lines the numbers have no frame.
   */
  context: string[]
  /** The borrowed identity when viewing as someone — it changes whose data this is. */
  privileged?: string | null
  currency: Currency
  kpis?: ExportKpi[]
  chart?: ExportChart
  tables: ExportTable[]
}

/* ---------------------------------------------------------------------------
   Formatting — ONE set of rules, used by both generators so the two files can
   never disagree. Mirrors components/ui.tsx: money is 0-dp with $ only in USD
   mode (local totals span currencies — stamping a code on them would lie).
   --------------------------------------------------------------------------- */

export const DASH = '—'

export function formatCell(v: string | number | null, kind: CellKind, currency: Currency): string {
  if (v === null) return DASH
  if (typeof v === 'string') return v
  switch (kind) {
    case 'money': {
      const digits = v.toLocaleString('en-US', { maximumFractionDigits: 0 })
      /* Dollars get the symbol; a chosen currency gets its code, because there
         is no symbol every reader would read the same way. Local gets neither:
         the figures may span currencies and stamping one would be a lie. */
      if (isUsd(currency)) return `$${digits}`
      const code = unitOf(currency)
      return code ? `${code} ${digits}` : digits
    }
    case 'int':
      return v.toLocaleString('en-US', { maximumFractionDigits: 0 })
    case 'pct':
      return `${(v * 100).toFixed(1)}%`
    case 'num2':
      return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    case 'qty':
      return v.toLocaleString('en-US', { maximumFractionDigits: 4 })
    default:
      return String(v)
  }
}

/** Excel number-format string per kind — raw numbers stay computable there. */
export function excelFormat(kind: CellKind, currency: Currency): string | null {
  switch (kind) {
    case 'money': {
      /* A NUMBER FORMAT, not text: the cell stays computable, which is the whole
         reason to extract to Excel rather than to a picture of a table. The code
         goes in the format string so a restated figure carries its unit into the
         spreadsheet — a column of bare numbers headed "Amount" is exactly how a
         reader ends up adding dirhams to dollars in their own workbook. */
      if (isUsd(currency)) return '"$"#,##0;[Red]-"$"#,##0'
      const code = unitOf(currency)
      return code ? `"${code} "#,##0;[Red]-"${code} "#,##0` : '#,##0;[Red]-#,##0'
    }
    case 'int':
      return '#,##0'
    case 'pct':
      return '0.0%'
    case 'num2':
      return '#,##0.00'
    /* Two decimals always shown and up to four kept: Excel has no format that
       drops the point from a whole number, and "1,200." reads as a typo. The
       cell holds the exact number either way. */
    case 'qty':
      return '#,##0.00##'
    default:
      return null
  }
}

/** `mbi-sales-explorer-20260809-1432.xlsx` — Windows-safe, sortable. */
export function exportFileName(doc: ExportDoc, ext: 'xlsx' | 'pdf'): string {
  const slug = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  const t = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  const stamp = `${t.getFullYear()}${p(t.getMonth() + 1)}${p(t.getDate())}-${p(t.getHours())}${p(t.getMinutes())}`
  return `mbi-${slug(doc.report)}-${slug(doc.page)}-${stamp}.${ext}`
}

/** "9 Aug 2026, 14:32" — stamped on every export so a printout can age honestly. */
export function generatedAt(): string {
  return new Date().toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoke on the next tick — revoking synchronously races the click in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}

/**
 * Rasterize an on-screen SVG chart for embedding. The serialized copy loses
 * the page stylesheet, so every element's COMPUTED paint is copied onto the
 * clone as attributes first — otherwise the brand colours (CSS variables)
 * silently become black. Returns null on any failure; the export then simply
 * carries the table without the picture.
 */
export async function chartFromSvg(svg: SVGSVGElement | null, caption?: string): Promise<ExportChart | undefined> {
  if (!svg) return undefined
  try {
    const rect = svg.getBoundingClientRect()
    const w = Math.max(1, Math.round(rect.width))
    const h = Math.max(1, Math.round(rect.height))
    const clone = svg.cloneNode(true) as SVGSVGElement
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    clone.setAttribute('width', String(w))
    clone.setAttribute('height', String(h))
    const src = svg.querySelectorAll<SVGElement>('*')
    const dst = clone.querySelectorAll<SVGElement>('*')
    src.forEach((el, i) => {
      const cs = getComputedStyle(el)
      const d = dst[i]
      if (!d) return
      d.setAttribute('fill', cs.fill)
      d.setAttribute('stroke', cs.stroke)
      d.setAttribute('stroke-width', cs.strokeWidth)
      d.setAttribute('opacity', cs.opacity)
      d.setAttribute('font-family', 'Helvetica, Arial, sans-serif')
      d.setAttribute('font-size', cs.fontSize)
      d.removeAttribute('class')
    })
    const xml = new XMLSerializer().serializeToString(clone)
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('svg rasterize failed'))
      img.src = url
    })
    const scale = 2 // crisp on print
    const canvas = document.createElement('canvas')
    canvas.width = w * scale
    canvas.height = h * scale
    const ctx = canvas.getContext('2d')
    if (!ctx) return undefined
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    // JPEG, not PNG: the white ground is already painted, and a raw PNG at 2x
    // put SIX megabytes of chart into a one-page PDF.
    return { dataUrl: canvas.toDataURL('image/jpeg', 0.92), width: w, height: h, caption }
  } catch {
    return undefined
  }
}
